import {
  defaultEngineConfig,
  defaultExportOptions,
  defaultRenderSettings,
  type EngineConfig,
  type EngineStats,
  type ExportOptions,
  type PrepareStats,
  type RenderSettings,
  type StopReason,
} from '@/types/engine';

export type EngineStatus = 'booting' | 'idle' | 'loading' | 'ready' | 'preparing' | 'running' | 'paused' | 'done' | 'error';
export type ViewMode = 'strings' | 'original' | 'processed' | 'approximation' | 'difference' | 'heatmap';
export type EditTool = 'select' | 'addString' | 'pan';
export type RendererKind = '2d' | '3d';
export type BlendMode = 'source-over' | 'multiply' | 'darken';

export interface SourceState {
  kind: 'image' | null;
  name: string;
  width: number;
  height: number;
  bitmap: ImageBitmap | null;
  rgba: Uint8Array | null;
}

export interface TargetState {
  width: number;
  height: number;
  resolution: number;
  cropMode: 'square' | 'fit';
  processed: Uint8Array | null;
  /** 8-bit lightness of the unprocessed source at target resolution. */
  original: Uint8Array | null;
}

export interface SolutionState {
  pins: Float32Array;
  /** Flat `[from, to, from, to, ...]`; may hold spare capacity beyond `count`. */
  sequence: Uint16Array;
  count: number;
  scores: Float32Array;
  errors: Float32Array;
  current: Uint8Array | null;
  error: number;
  initialError: number;
  currentPin: number;
  stopReason: StopReason | null;
  locked: ReadonlySet<number>;
}

export interface PlaybackState {
  index: number;
  playing: boolean;
  speed: number;
  follow: boolean;
}

export interface UiState {
  view: ViewMode;
  showPins: boolean;
  showPinIds: boolean;
  showCandidates: boolean;
  underlayOpacity: number;
  tool: EditTool;
  selected: number | null;
  hovered: number | null;
  pendingPin: number | null;
  renderer: RendererKind;
  debugOpen: boolean;
  blendMode: BlendMode;
  stringRoughness: number;
  stringBrightness: number;
  activePanel: string;
}

export interface StatsState {
  stepsPerSecond: number;
  elapsedMs: number;
  wasmMemoryBytes: number;
  batchSize: number;
  fps: number;
  engine: EngineStats | null;
  prepare: PrepareStats | null;
  prepareMs: number;
  loadMs: number;
  renderMs: number;
}

/** none: nothing worth saving yet; saving: a write is due or in flight; saved; error: the last write failed. */
export type SaveState = 'none' | 'saving' | 'saved' | 'error';

/** The design this editor session belongs to in the browser's library. */
export interface DesignState {
  /** Assigned by the first save. */
  id: string | null;
  name: string;
  save: SaveState;
}

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string;
  redoLabel: string;
}

export interface AppState {
  status: EngineStatus;
  statusMessage: string;
  error: string | null;
  wasmVersion: string;
  config: EngineConfig;
  render: RenderSettings;
  exportOptions: ExportOptions;
  source: SourceState;
  target: TargetState;
  solution: SolutionState;
  playback: PlaybackState;
  ui: UiState;
  stats: StatsState;
  history: HistoryState;
  design: DesignState;
  needsPrepare: boolean;
}

export function emptySolution(): SolutionState {
  return {
    pins: new Float32Array(0),
    sequence: new Uint16Array(0),
    count: 0,
    scores: new Float32Array(0),
    errors: new Float32Array(0),
    current: null,
    error: 0,
    initialError: 0,
    currentPin: 0,
    stopReason: null,
    locked: new Set(),
  };
}

export function initialState(): AppState {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  const resolution = Number(env.VITE_DEFAULT_RESOLUTION) || 512;
  return {
    status: 'booting',
    statusMessage: 'Starting engine…',
    error: null,
    wasmVersion: '',
    config: defaultEngineConfig(),
    render: defaultRenderSettings(),
    exportOptions: defaultExportOptions(),
    source: { kind: null, name: '', width: 0, height: 0, bitmap: null, rgba: null },
    target: { width: 0, height: 0, resolution, cropMode: 'square', processed: null, original: null },
    solution: emptySolution(),
    playback: { index: 0, playing: false, speed: 400, follow: true },
    ui: {
      view: 'strings',
      showPins: true,
      showPinIds: false,
      showCandidates: false,
      underlayOpacity: 0.35,
      tool: 'select',
      selected: null,
      hovered: null,
      pendingPin: null,
      renderer: '2d',
      debugOpen: env.VITE_DEBUG_PANEL === 'true',
      blendMode: 'source-over',
      stringRoughness: 0.15,
      stringBrightness: 0,
      activePanel: 'input',
    },
    stats: {
      stepsPerSecond: 0,
      elapsedMs: 0,
      wasmMemoryBytes: 0,
      batchSize: 0,
      fps: 0,
      engine: null,
      prepare: null,
      prepareMs: 0,
      loadMs: 0,
      renderMs: 0,
    },
    history: { canUndo: false, canRedo: false, undoLabel: '', redoLabel: '' },
    design: { id: null, name: '', save: 'none' },
    needsPrepare: true,
  };
}

export function sequenceView(solution: SolutionState): Uint16Array {
  return solution.sequence.subarray(0, solution.count * 2);
}
